import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Heart, Home } from 'lucide-react';
import SignaturePad from '../components/SignaturePad';

export default function CNAApplicationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'NC',
    zip: '',
    ssn: '',
    // Professional Information
    cnaLicenseNumber: '',
    licenseState: 'NC',
    licenseExpiration: '',
    yearsExperience: '',
    // Registry Questions
    ncNurseAideI: '',
    ncNurseAideII: '',
    substantiatedFinding: '',
    // Training Information
    trainingProgram: '',
    trainingLocation: '',
    // Employment
    availability: '',
    startDate: '',
    specializations: '',
    // Relevant Experience
    employer1: '',
    employer1DatesFrom: '',
    employer1DatesTo: '',
    keyDuties1: '',
    employer2: '',
    employer2DatesFrom: '',
    employer2DatesTo: '',
    keyDuties2: '',
    // Additional
    whyJoin: '',
    references: ''
  });

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate signature
    if (!signatureData) {
      toast.error('Please sign the application before submitting.');
      return;
    }
    
    setLoading(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('position_type', 'cna');
      formDataToSend.append('first_name', formData.firstName);
      formDataToSend.append('middle_name', formData.middleName || '');
      formDataToSend.append('last_name', formData.lastName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('address', formData.address);
      formDataToSend.append('city', formData.city);
      formDataToSend.append('state', formData.state);
      formDataToSend.append('zip_code', formData.zip);
      formDataToSend.append('ssn', formData.ssn);
      formDataToSend.append('license_number', formData.cnaLicenseNumber);
      formDataToSend.append('license_state', formData.licenseState);
      formDataToSend.append('license_expiration', formData.licenseExpiration);
      formDataToSend.append('years_experience', formData.yearsExperience);
      formDataToSend.append('nc_nurse_aide_i', formData.ncNurseAideI);
      formDataToSend.append('nc_nurse_aide_ii', formData.ncNurseAideII);
      formDataToSend.append('substantiated_finding', formData.substantiatedFinding);
      formDataToSend.append('training_program', formData.trainingProgram);
      formDataToSend.append('training_location', formData.trainingLocation);
      formDataToSend.append('specializations', formData.specializations || '');
      formDataToSend.append('availability', formData.availability);
      formDataToSend.append('start_date', formData.startDate);
      formDataToSend.append('employer_1', formData.employer1 || '');
      formDataToSend.append('employer_1_dates_from', formData.employer1DatesFrom || '');
      formDataToSend.append('employer_1_dates_to', formData.employer1DatesTo || '');
      formDataToSend.append('key_duties_1', formData.keyDuties1 || '');
      formDataToSend.append('employer_2', formData.employer2 || '');
      formDataToSend.append('employer_2_dates_from', formData.employer2DatesFrom || '');
      formDataToSend.append('employer_2_dates_to', formData.employer2DatesTo || '');
      formDataToSend.append('key_duties_2', formData.keyDuties2 || '');
      formDataToSend.append('why_join', formData.whyJoin || '');
      formDataToSend.append('references', formData.references || '');
      formDataToSend.append('signature_data', signatureData);
      
      if (resumeFile) {
        formDataToSend.append('resume', resumeFile);
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/applications/submit`, {
        method: 'POST',
        body: formDataToSend
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit application');
      }
      
      toast.success('Application submitted successfully! We will contact you soon.');
      navigate('/careers');
    } catch (error) {
      toast.error(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-purple-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-purple-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_caregiver-command/artifacts/prj1fr46_Integrity%20Home%20Care%20Logo%20Webpage.png"
                alt="Integrity Home Care LLC"
                className="h-16 w-auto"
              />
            </div>
            <Button
              onClick={() => navigate('/careers')}
              variant="outline"
              className="border-purple-600 text-purple-600 hover:bg-purple-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Careers
            </Button>
          </div>
        </div>
      </nav>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-purple-900 mb-4" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            CNA Application
          </h1>
          <p className="text-lg text-gray-700">
            Certified Nursing Assistant - In-Home Caregiver Position
          </p>
        </div>

        <Card className="p-8 border-purple-100">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Information */}
            <div>
              <h2 className="text-2xl font-bold text-purple-900 mb-6">Personal Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <Label>Middle Name</Label>
                  <Input
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label>Street Address *</Label>
                <Input
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <Label>City *</Label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label>State *</Label>
                  <Input
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label>ZIP Code *</Label>
                  <Input
                    name="zip"
                    value={formData.zip}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label>Social Security Number *</Label>
                <Input
                  name="ssn"
                  value={formData.ssn}
                  onChange={handleChange}
                  required
                  placeholder="XXX-XX-XXXX"
                  maxLength={11}
                />
                <p className="text-xs text-gray-500 mt-1">Required for background check and employment verification</p>
              </div>
            </div>

            {/* Professional Credentials */}
            <div>
              <h2 className="text-2xl font-bold text-purple-900 mb-6">Professional Credentials</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>CNA License Number *</Label>
                  <Input
                    name="cnaLicenseNumber"
                    value={formData.cnaLicenseNumber}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label>License State *</Label>
                  <Input
                    name="licenseState"
                    value={formData.licenseState}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label>License Expiration Date *</Label>
                  <Input
                    type="date"
                    name="licenseExpiration"
                    value={formData.licenseExpiration}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label>Years of CNA Experience *</Label>
                  <Input
                    type="number"
                    name="yearsExperience"
                    value={formData.yearsExperience}
                    onChange={handleChange}
                    required
                    min="0"
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label>Areas of Specialization or Experience</Label>
                <Textarea
                  name="specializations"
                  value={formData.specializations}
                  onChange={handleChange}
                  rows="3"
                  placeholder="e.g., Elderly care, post-surgical care, chronic illness management, etc."
                />
              </div>
            </div>

            {/* NC Registry Information */}
            <div>
              <h2 className="text-2xl font-bold text-purple-900 mb-6">NC Registry Information</h2>
              <div className="space-y-4">
                <div>
                  <Label>Are you currently listed on the NC Nurse Aide I Registry? *</Label>
                  <div className="flex gap-6 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="ncNurseAideI"
                        value="yes"
                        checked={formData.ncNurseAideI === 'yes'}
                        onChange={handleChange}
                        required
                        className="w-4 h-4 text-purple-600"
                      />
                      <span>Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="ncNurseAideI"
                        value="no"
                        checked={formData.ncNurseAideI === 'no'}
                        onChange={handleChange}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>

                <div>
                  <Label>Are you currently listed on the NC Nurse Aide II Registry? *</Label>
                  <div className="flex gap-6 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="ncNurseAideII"
                        value="yes"
                        checked={formData.ncNurseAideII === 'yes'}
                        onChange={handleChange}
                        required
                        className="w-4 h-4 text-purple-600"
                      />
                      <span>Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="ncNurseAideII"
                        value="no"
                        checked={formData.ncNurseAideII === 'no'}
                        onChange={handleChange}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>

                <div>
                  <Label>Have you ever had a substantiated finding of abuse, neglect, or misappropriation of property listed on any registry? *</Label>
                  <div className="flex gap-6 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="substantiatedFinding"
                        value="yes"
                        checked={formData.substantiatedFinding === 'yes'}
                        onChange={handleChange}
                        required
                        className="w-4 h-4 text-purple-600"
                      />
                      <span>Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="substantiatedFinding"
                        value="no"
                        checked={formData.substantiatedFinding === 'no'}
                        onChange={handleChange}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Training Information */}
            <div>
              <h2 className="text-2xl font-bold text-purple-900 mb-6">Training Program Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Training Program Completed *</Label>
                  <Input
                    name="trainingProgram"
                    value={formData.trainingProgram}
                    onChange={handleChange}
                    required
                    placeholder="Name of CNA training program"
                  />
                </div>
                <div>
                  <Label>Training Location *</Label>
                  <Input
                    name="trainingLocation"
                    value={formData.trainingLocation}
                    onChange={handleChange}
                    required
                    placeholder="City, State"
                  />
                </div>
              </div>
            </div>

            {/* Relevant Experience */}
            <div>
              <h2 className="text-2xl font-bold text-purple-900 mb-6">Relevant Experience</h2>
              <p className="text-gray-600 mb-4">Please list any experience providing hands-on care (bathing, dressing, toileting, etc.)</p>
              
              {/* Employer 1 */}
              <div className="p-4 border border-purple-100 rounded-lg mb-4">
                <h3 className="font-semibold text-purple-800 mb-3">Previous Employer 1</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Employer Name</Label>
                    <Input
                      name="employer1"
                      value={formData.employer1}
                      onChange={handleChange}
                      placeholder="Company/Facility name"
                    />
                  </div>
                  <div>
                    <Label>Dates From</Label>
                    <Input
                      type="date"
                      name="employer1DatesFrom"
                      value={formData.employer1DatesFrom}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <Label>Dates To</Label>
                    <Input
                      type="date"
                      name="employer1DatesTo"
                      value={formData.employer1DatesTo}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <Label>Key Duties</Label>
                  <Textarea
                    name="keyDuties1"
                    value={formData.keyDuties1}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Describe your main responsibilities..."
                  />
                </div>
              </div>

              {/* Employer 2 */}
              <div className="p-4 border border-purple-100 rounded-lg">
                <h3 className="font-semibold text-purple-800 mb-3">Previous Employer 2</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Employer Name</Label>
                    <Input
                      name="employer2"
                      value={formData.employer2}
                      onChange={handleChange}
                      placeholder="Company/Facility name"
                    />
                  </div>
                  <div>
                    <Label>Dates From</Label>
                    <Input
                      type="date"
                      name="employer2DatesFrom"
                      value={formData.employer2DatesFrom}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <Label>Dates To</Label>
                    <Input
                      type="date"
                      name="employer2DatesTo"
                      value={formData.employer2DatesTo}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <Label>Key Duties</Label>
                  <Textarea
                    name="keyDuties2"
                    value={formData.keyDuties2}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Describe your main responsibilities..."
                  />
                </div>
              </div>
            </div>

            {/* Employment Information */}
            <div>
              <h2 className="text-2xl font-bold text-purple-900 mb-6">Employment Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Availability *</Label>
                  <select
                    name="availability"
                    value={formData.availability}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select availability</option>
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="per-diem">Per Diem</option>
                    <option value="weekend">Weekends only</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
                <div>
                  <Label>Available Start Date *</Label>
                  <Input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Additional Questions */}
            <div>
              <h2 className="text-2xl font-bold text-purple-900 mb-6">Additional Information</h2>
              <div className="space-y-4">
                <div>
                  <Label>Why do you want to join Integrity Home Care LLC? *</Label>
                  <Textarea
                    name="whyJoin"
                    value={formData.whyJoin}
                    onChange={handleChange}
                    rows="4"
                    required
                    placeholder="Tell us what draws you to our faith-based mission and how you align with our values..."
                  />
                </div>

                <div>
                  <Label>Professional References (Name, Relationship, Phone Number) *</Label>
                  <Textarea
                    name="references"
                    value={formData.references}
                    onChange={handleChange}
                    rows="3"
                    required
                    placeholder="Please provide at least two professional references..."
                  />
                </div>

                <div>
                  <Label>Upload Resume or CV *</Label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setResumeFile(e.target.files[0])}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-sm text-gray-600 mt-2">Accepted formats: PDF, DOC, DOCX (Max 5MB)</p>
                </div>
              </div>
            </div>

            {/* Signature Section */}
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-purple-900 flex items-center gap-2">
                  <span className="p-2 bg-purple-100 rounded-full">
                    <Heart className="w-5 h-5 text-purple-600" />
                  </span>
                  Signature & Agreement
                </h2>
                <p className="text-gray-600 mt-2">
                  Please read the agreement below and provide your electronic signature to complete your application.
                </p>
              </div>
              
              <SignaturePad 
                onSignatureChange={setSignatureData}
                label="Applicant Signature"
              />
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-purple-100">
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg"
                data-testid="submit-application-button"
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
              <p className="text-sm text-gray-600 text-center mt-4">
                By submitting this application, you acknowledge that all information provided is accurate and complete.
              </p>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}